import { useCallback, useEffect, useRef, useState } from "react";
import {
  Award, Plus, Search, Pencil, Trash2, Loader2, Check, X,
  FileQuestion, Clock, CheckCircle2, AlertCircle, HelpCircle,
  FileCode, Upload, Copy, Sparkles, RefreshCw,
  CheckSquare, Square, Minus,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError, type CocExam, type CocQuestion } from "../../lib/api";
import { AdminEmptyState, AdminPageHeader } from "../../components/admin/AdminUI";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { Button } from "../../components/ui/Button";
import { QuestionFigure } from "../../components/QuestionFigure";
import { QuestionFigureUploader } from "../../components/admin/QuestionFigureUploader";
import { AdminPassageSelector } from "../../components/admin/AdminPassageSelector";
import { Modal } from "../../components/ui/Modal";
import { IngestedBadge } from "../../components/admin/IngestedBadge";
import { useIngestedMarkers } from "../../lib/useIngestedMarkers";

function useAdminToken() {
  const { tokens } = useAuth();
  return tokens?.accessToken ?? null;
}

const SAMPLE_JSON_TEMPLATE = JSON.stringify(
  [
    {
      text: "Which of the following is the primary indicator of maternal health status in a community?",
      options: [
        "Maternal Mortality Ratio (MMR)",
        "Infant Mortality Rate (IMR)",
        "Total Fertility Rate (TFR)",
        "Crude Death Rate (CDR)"
      ],
      correct_answer: 0,
      explanation: "Maternal Mortality Ratio (MMR) directly measures maternal deaths per 100,000 live births."
    },
    {
      text: "What is the recommended storage temperature for standard vaccines in cold chain management at health center level?",
      options: [
        "-20°C to -10°C",
        "+2°C to +8°C",
        "+15°C to +25°C",
        "0°C to +4°C"
      ],
      correct_answer: 1,
      explanation: "Standard vaccines at health centers must be stored between +2°C and +8°C."
    }
  ],
  null,
  2
);

function normalizeJsonQuestions(rawText: string): { questions: CocQuestion[]; error?: string } {
  if (!rawText.trim()) {
    return { questions: [] };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch (e) {
    return { questions: [], error: `Invalid JSON syntax: ${(e as Error).message}` };
  }

  let listObj = parsed;
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const obj = parsed as Record<string, unknown>;
    if (Array.isArray(obj.questions)) {
      listObj = obj.questions;
    } else if (Array.isArray(obj.items)) {
      listObj = obj.items;
    } else if (Array.isArray(obj.data)) {
      listObj = obj.data;
    }
  }

  if (!Array.isArray(listObj)) {
    return { questions: [], error: "JSON must be an array of question objects (e.g. [{ text, options, correct_answer }])" };
  }

  const result: CocQuestion[] = [];
  for (let i = 0; i < listObj.length; i++) {
    const q = listObj[i];
    if (!q || typeof q !== "object") continue;

    const text = String(q.text || q.question || q.prompt || q.title || "").trim();
    if (!text) {
      return { questions: [], error: `Question #${i + 1} is missing a text/question prompt.` };
    }

    let options: string[] = [];
    if (Array.isArray(q.options)) {
      options = q.options.map((opt: unknown) => {
        if (typeof opt === "object" && opt !== null && "text" in opt) {
          return String((opt as { text: unknown }).text).trim();
        }
        return String(opt).trim();
      }).filter(Boolean);
    } else if (q.choices && Array.isArray(q.choices)) {
      options = q.choices.map((opt: unknown) => {
        if (typeof opt === "object" && opt !== null && "text" in opt) {
          return String((opt as { text: unknown }).text).trim();
        }
        return String(opt).trim();
      }).filter(Boolean);
    }

    if (options.length < 2) {
      return { questions: [], error: `Question #${i + 1} ("${text.slice(0, 30)}…") must have at least 2 options.` };
    }

    let correctIdx = 0;
    const rawAnswer = q.correct_answer ?? q.correct ?? q.answer ?? q.correctIndex ?? 0;
    if (typeof rawAnswer === "number") {
      correctIdx = rawAnswer >= 0 && rawAnswer < options.length ? rawAnswer : 0;
    } else if (typeof rawAnswer === "string") {
      const trimmed = rawAnswer.trim().toUpperCase();
      if (trimmed === "A") correctIdx = 0;
      else if (trimmed === "B") correctIdx = 1;
      else if (trimmed === "C") correctIdx = 2;
      else if (trimmed === "D") correctIdx = 3;
      else {
        const found = options.findIndex((opt) => opt.toLowerCase() === rawAnswer.trim().toLowerCase());
        if (found !== -1) correctIdx = found;
        else {
          const parsedNum = parseInt(trimmed, 10);
          if (!isNaN(parsedNum) && parsedNum >= 0 && parsedNum < options.length) {
            correctIdx = parsedNum;
          }
        }
      }
    }

    const explanation = q.explanation ? String(q.explanation).trim() : undefined;

    let figStatus: "none" | "required" | "uploaded" = "none";
    const figImage: string | null = (q.figure_image as string) || (q.figureImage as string) || null;
    let figAlt: string | null = (q.figure_alt as string) || (q.figureAlt as string) || null;
    let figLoc: any = q.figure_location || q.figureLocation || null;

    if (q.figure && typeof q.figure === "object") {
      const f = q.figure as Record<string, unknown>;
      if (f.required === true || f.required === "true") {
        figStatus = figImage ? "uploaded" : "required";
      }
      if (f.alt) figAlt = String(f.alt).trim();
      if (f.location && typeof f.location === "object") {
        const loc = f.location as Record<string, unknown>;
        if (loc.pdfPage != null) {
          const pageNum = Number(loc.pdfPage);
          if (!Number.isInteger(pageNum) || pageNum <= 0) {
            return { questions: [], error: `Question #${i + 1}: figure.location.pdfPage must be a positive integer.` };
          }
        }
        figLoc = loc;
      }
    }

    const directStatus = q.figure_status || q.figureStatus;
    if (directStatus === "required" || directStatus === "uploaded" || directStatus === "none") {
      figStatus = directStatus as "none" | "required" | "uploaded";
    }
    if (figImage && figStatus !== "none") {
      figStatus = "uploaded";
    }

    const passageId = (q as any).passageId || (q as any).passage_id || null;
    const passageKey = (q as any).passageKey || (q as any).passage_key || null;

    result.push({
      id: q.id ? String(q.id) : `q_${i + 1}_${Math.random().toString(36).substring(2, 8)}`,
      text,
      options,
      correct_answer: correctIdx,
      explanation,
      passage_id: passageId,
      passageId: passageId,
      passage_key: passageKey,
      passageKey: passageKey,
      figure_status: figStatus,
      figure_image: figImage,
      figure_alt: figAlt,
      figure_location: figLoc,
    });
  }

  return { questions: result };
}

export function AdminCocPage() {
  const token = useAdminToken();
  const ingested = useIngestedMarkers(token);
  const [exams, setExams] = useState<CocExam[]>([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExam, setEditingExam] = useState<CocExam | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedExams, setSelectedExams] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  const loadExams = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError("");
    api.admin.coc.list(token)
      .then((data) => setExams(data))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load COC exams"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const categories = Array.from(new Set(exams.map((e) => e.category).filter(Boolean)));

  const filteredExams = exams.filter((e) => {
    const matchCat = categoryFilter === "all" || e.category.toLowerCase() === categoryFilter.toLowerCase();
    if (!matchCat) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (e.title && e.title.toLowerCase().includes(q)) ||
      (e.category && e.category.toLowerCase().includes(q)) ||
      (e.year && String(e.year).includes(q)) ||
      (e.description && e.description.toLowerCase().includes(q))
    );
  });

  const allSelected = filteredExams.length > 0 && filteredExams.every((e) => selectedExams.has(e.id));
  const someSelected = filteredExams.some((e) => selectedExams.has(e.id));

  function toggleAllExams() {
    if (allSelected) {
      setSelectedExams(new Set());
    } else {
      setSelectedExams(new Set(filteredExams.map((e) => e.id)));
    }
  }

  function toggleExam(id: string) {
    setSelectedExams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function runBulkCocAction(action: "publish" | "unpublish") {
    if (!token || selectedExams.size === 0) return;
    setBulkActionLoading(true);
    setError("");
    try {
      await api.admin.coc.bulk(token, {
        exam_ids: Array.from(selectedExams),
        action,
      });
      setSelectedExams(new Set());
      loadExams();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `Failed to ${action} exams`);
    } finally {
      setBulkActionLoading(false);
    }
  }

  async function handleBulkDeleteConfirm() {
    if (!token || selectedExams.size === 0) return;
    setBulkActionLoading(true);
    setError("");
    try {
      await api.admin.coc.bulk(token, {
        exam_ids: Array.from(selectedExams),
        action: "delete",
      });
      setSelectedExams(new Set());
      setBulkDeleteConfirmOpen(false);
      loadExams();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to bulk delete exams");
    } finally {
      setBulkActionLoading(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!token || !deleteId) return;
    setDeleting(true);
    try {
      await api.admin.coc.delete(token, deleteId);
      setExams((prev) => prev.filter((e) => e.id !== deleteId));
      setDeleteId(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to delete exam");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-8 space-y-6">
      <AdminPageHeader
        title="COC Standalone Exams"
        subtitle="Manage Certificate of Competency national exit exams (independent of university courses)"
        actions={
          <button
            type="button"
            onClick={() => {
              setEditingExam(null);
              setModalOpen(true);
            }}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold shadow-sm hover:opacity-90 transition flex items-center gap-2"
          >
            <Plus size={16} /> Create COC Exam
          </button>
        }
      />

      {error && (
        <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")} className="text-destructive font-bold">×</button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, category, year…"
            className="w-full pl-9 pr-8 py-2 rounded-lg border border-border text-sm outline-none focus:border-primary"
          />
          {Boolean(search) && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <label className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Category:</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
          >
            <option value="all">All Categories ({exams.length})</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk actions toolbar */}
      {selectedExams.size > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs font-bold text-primary">
            {selectedExams.size} exam(s) selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={bulkActionLoading}
              onClick={() => runBulkCocAction("publish")}
              className="px-3 py-1.5 bg-success hover:bg-success text-success-foreground rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
            >
              <Check size={13} /> Publish
            </button>
            <button
              type="button"
              disabled={bulkActionLoading}
              onClick={() => runBulkCocAction("unpublish")}
              className="px-3 py-1.5 border border-border bg-card text-foreground hover:bg-muted rounded-lg text-xs font-semibold transition disabled:opacity-50"
            >
              Unpublish
            </button>
            <button
              type="button"
              disabled={bulkActionLoading}
              onClick={() => setBulkDeleteConfirmOpen(true)}
              className="px-3 py-1.5 bg-destructive hover:bg-destructive text-destructive-foreground rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
            >
              <Trash2 size={13} /> Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedExams(new Set())}
              className="px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground font-semibold"
            >
              Deselect
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center text-muted-foreground">
          <Loader2 className="animate-spin mr-2" /> Loading COC exams…
        </div>
      ) : filteredExams.length === 0 ? (
        <AdminEmptyState
          message={search || categoryFilter !== "all" ? "No COC exams match your search filters." : "No COC exams found. Click Create COC Exam to add the first exam."}
        />
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground font-semibold">
                <tr>
                  <th className="px-4 py-4 w-10 text-center">
                    <button
                      type="button"
                      onClick={toggleAllExams}
                      className="p-1 rounded hover:bg-muted text-muted-foreground transition"
                      title={allSelected ? "Deselect all" : "Select all"}
                    >
                      {allSelected ? (
                        <CheckSquare size={16} className="text-primary" />
                      ) : someSelected ? (
                        <Minus size={16} className="text-primary" />
                      ) : (
                        <Square size={16} />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-4">Exam Title</th>
                  <th className="px-6 py-4">Category / Field</th>
                  <th className="px-6 py-4">Year</th>
                  <th className="px-6 py-4">Questions</th>
                  <th className="px-6 py-4">Time Limit</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredExams.map((exam) => (
                  <tr key={exam.id} className="hover:bg-muted/30 transition">
                    <td className="px-4 py-4 w-10 text-center">
                      <button
                        type="button"
                        onClick={() => toggleExam(exam.id)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground transition"
                      >
                        {selectedExams.has(exam.id) ? (
                          <CheckSquare size={16} className="text-primary" />
                        ) : (
                          <Square size={16} />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <Award size={18} />
                        </div>
                        <div>
                          <p className="font-bold text-foreground">{exam.title} {ingested.coc.has(exam.id) && <IngestedBadge />}</p>
                          {exam.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">{exam.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-muted-foreground">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary text-secondary-foreground">
                        {exam.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{exam.year || "—"}</td>
                    <td className="px-6 py-4 font-medium">
                      <span className="flex items-center gap-1.5">
                        <FileQuestion size={14} className="text-primary" />
                        {exam.question_count} MCQs
                      </span>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {exam.time_limit_minutes ? `${exam.time_limit_minutes} mins` : "Untimed"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {exam.is_published ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-success/10 text-success">
                            Published
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-foreground">
                            Draft
                          </span>
                        )}
                        {!exam.is_free && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-warning/10 text-warning">
                            PRO
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingExam(exam);
                            setModalOpen(true);
                          }}
                          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
                          title="Edit exam"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteId(exam.id)}
                          className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition"
                          title="Delete exam"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmDialog
        open={!!deleteId}
        title="Delete COC Exam"
        message="Are you sure you want to delete this COC exam? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteId(null)}
      />

      {/* Bulk Delete confirmation modal */}
      <ConfirmDialog
        open={bulkDeleteConfirmOpen}
        title={`Delete ${selectedExams.size} COC Exams?`}
        message={`Are you sure you want to delete the selected ${selectedExams.size} exams? All questions associated with them will also be deleted.`}
        confirmLabel={`Delete (${selectedExams.size})`}
        variant="destructive"
        loading={bulkActionLoading}
        onConfirm={handleBulkDeleteConfirm}
        onClose={() => setBulkDeleteConfirmOpen(false)}
      />

      {/* Create / Edit Modal */}
      {modalOpen && (
        <CocExamModal
          exam={editingExam}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            loadExams();
          }}
        />
      )}
    </div>
  );
}

function CocExamModal({
  exam,
  onClose,
  onSaved,
}: {
  exam: CocExam | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const token = useAdminToken();
  const ingestedCoc = useIngestedMarkers(token);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(exam?.title ?? "");
  const [category, setCategory] = useState(exam?.category ?? "Public Health");
  const [semester, setSemester] = useState(String(exam?.semester ?? 1));
  const [year, setYear] = useState(exam?.year ?? new Date().getFullYear().toString());
  const [description, setDescription] = useState(exam?.description ?? "");
  const [timeLimit, setTimeLimit] = useState(exam?.time_limit_minutes ? String(exam.time_limit_minutes) : "60");
  const [passPercentage, setPassPercentage] = useState(exam?.pass_percentage ? String(exam.pass_percentage) : "50");
  const [isPublished, setIsPublished] = useState(exam?.is_published ?? true);
  const [isFree, setIsFree] = useState(exam?.is_free ?? true);
  const [questions, setQuestions] = useState<CocQuestion[]>(exam?.questions ?? []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"details" | "questions">("details");

  // Questions input mode: 'json' (default) or 'form'
  const [inputMode, setInputMode] = useState<"json" | "form">("json");
  const [jsonInput, setJsonInput] = useState(
    exam?.questions && exam.questions.length > 0
      ? JSON.stringify(exam.questions, null, 2)
      : ""
  );
  const [jsonError, setJsonError] = useState("");
  const [jsonSuccessMsg, setJsonSuccessMsg] = useState("");

  // Question editing form draft state (for form mode)
  const [qText, setQText] = useState("");
  const [qOptA, setQOptA] = useState("");
  const [qOptB, setQOptB] = useState("");
  const [qOptC, setQOptC] = useState("");
  const [qOptD, setQOptD] = useState("");
  const [qCorrect, setQCorrect] = useState(0);
  const [qExplanation, setQExplanation] = useState("");
  const [qFigureStatus, setQFigureStatus] = useState<"none" | "required" | "uploaded">("none");
  const [qFigureImage, setQFigureImage] = useState<string | null>(null);
  const [qFigureAlt, setQFigureAlt] = useState<string>("");
  const [qFigureLocation, setQFigureLocation] = useState<any>(null);
  const [qPassageId, setQPassageId] = useState<string | null>(null);
  const [editingQIndex, setEditingQIndex] = useState<number | null>(null);
  const [qFormError, setQFormError] = useState("");

  function resetQuestionDraft() {
    setQFormError("");
    setQText("");
    setQOptA("");
    setQOptB("");
    setQOptC("");
    setQOptD("");
    setQCorrect(0);
    setQExplanation("");
    setQFigureStatus("none");
    setQFigureImage(null);
    setQFigureAlt("");
    setQFigureLocation(null);
    setQPassageId(null);
    setEditingQIndex(null);
  }

  function handleApplyJson() {
    setJsonError("");
    setJsonSuccessMsg("");
    const parsed = normalizeJsonQuestions(jsonInput);
    if (parsed.error) {
      setJsonError(parsed.error);
      return;
    }
    setQuestions(parsed.questions);
    setJsonSuccessMsg(`Successfully parsed and loaded ${parsed.questions.length} questions!`);
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        setJsonInput(content);
        setJsonError("");
        setJsonSuccessMsg("");
        const parsed = normalizeJsonQuestions(content);
        if (parsed.error) {
          setJsonError(parsed.error);
        } else {
          setQuestions(parsed.questions);
          setJsonSuccessMsg(`File loaded: ${parsed.questions.length} questions parsed!`);
        }
      }
    };
    reader.readAsText(file);
  }

  function handleLoadSampleJson() {
    setJsonInput(SAMPLE_JSON_TEMPLATE);
    setJsonError("");
    const parsed = normalizeJsonQuestions(SAMPLE_JSON_TEMPLATE);
    setQuestions(parsed.questions);
    setJsonSuccessMsg("Sample JSON template loaded!");
  }

  function handleSaveQuestion() {
    if (!qText.trim() || !qOptA.trim() || !qOptB.trim()) {
      setQFormError("Question text and at least options A & B are required.");
      return;
    }
    setQFormError("");
    const newQuestion: CocQuestion = {
      id: editingQIndex !== null ? questions[editingQIndex]?.id : undefined,
      text: qText.trim(),
      options: [qOptA.trim(), qOptB.trim(), qOptC.trim(), qOptD.trim()].filter(Boolean),
      correct_answer: qCorrect,
      explanation: qExplanation.trim() || undefined,
      passage_id: qPassageId,
      passageId: qPassageId,
      figure_status: qFigureStatus,
      figure_image: qFigureImage,
      figure_alt: qFigureAlt,
      figure_location: qFigureLocation,
    };

    let updated: CocQuestion[];
    if (editingQIndex !== null) {
      updated = questions.map((item, i) => (i === editingQIndex ? newQuestion : item));
    } else {
      updated = [...questions, newQuestion];
    }
    setQuestions(updated);
    setJsonInput(JSON.stringify(updated, null, 2));
    resetQuestionDraft();
  }

  function handleEditQuestion(index: number) {
    const q = questions[index];
    if (!q) return;
    setInputMode("form");
    setEditingQIndex(index);
    setQText(q.text);
    setQOptA(q.options[0] || "");
    setQOptB(q.options[1] || "");
    setQOptC(q.options[2] || "");
    setQOptD(q.options[3] || "");
    setQCorrect(q.correct_answer || 0);
    setQExplanation(q.explanation || "");
    setQFigureStatus(q.figure_status || "none");
    setQFigureImage(q.figure_image || null);
    setQFigureAlt(q.figure_alt || "");
    setQFigureLocation(q.figure_location || null);
    setQPassageId((q as any).passageId || (q as any).passage_id || null);
  }

  function handleRemoveQuestion(index: number) {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
    setJsonInput(JSON.stringify(updated, null, 2));
    if (editingQIndex === index) resetQuestionDraft();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (!title.trim() || !category.trim()) {
      setError("Title and Category are required.");
      return;
    }

    // Ensure JSON input is synced if user was typing in JSON mode
    let finalQuestions = questions;
    if (inputMode === "json" && jsonInput.trim()) {
      const parsed = normalizeJsonQuestions(jsonInput);
      if (parsed.error) {
        setActiveTab("questions");
        setJsonError(parsed.error);
        return;
      }
      finalQuestions = parsed.questions;
    }

    const missingFigures = finalQuestions.filter((q) => q.figure_status === "required" && !q.figure_image);
    if (isPublished && missingFigures.length > 0) {
      setError(`Cannot publish COC exam: ${missingFigures.length} question(s) need an image.`);
      setActiveTab("questions");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      title: title.trim(),
      category: category.trim(),
      semester: parseInt(semester, 10) || 1,
      year: year.trim() || undefined,
      description: description.trim() || undefined,
      time_limit_minutes: timeLimit ? parseInt(timeLimit, 10) : undefined,
      pass_percentage: passPercentage ? parseInt(passPercentage, 10) : 50,
      is_published: isPublished,
      is_free: isFree,
      questions: finalQuestions,
    };

    try {
      if (exam) {
        await api.admin.coc.update(token, exam.id, payload);
      } else {
        await api.admin.coc.create(token, payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save exam");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="xl"
      showCloseButton={false}
      className="p-0 overflow-hidden max-w-3xl max-h-[90vh]"
    >
      <div className="w-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/40">
          <div>
            <h3 className="font-bold text-lg">{exam ? "Edit COC Exam" : "Create Standalone COC Exam"} {exam && ingestedCoc.coc.has(exam.id) && <IngestedBadge />}</h3>
            <p className="text-xs text-muted-foreground">Specify exam details and paste/upload JSON questions</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
            <X size={18} />
          </button>
        </div>

        {/* Navigation tabs */}
        <div className="flex border-b border-border px-6 bg-muted/20">
          <button
            type="button"
            onClick={() => setActiveTab("details")}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition ${
              activeTab === "details"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            1. Exam Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("questions")}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition flex items-center gap-2 ${
              activeTab === "questions"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            2. Multiple Choice Questions
            <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-bold">
              {questions.length}
            </span>
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-xs border border-destructive/30">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === "details" ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Exam Title *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Public Health Officers Exit/COC Exam 2023"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Category / Department *</label>
                  <input
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Public Health, Nursing, Software Engineering, Accounting"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Exam Year</label>
                  <input
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="e.g. 2023"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Semester *</label>
                  <select
                    value={semester}
                    onChange={(e) => setSemester(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary bg-card"
                    required
                  >
                    <option value="1">Semester 1</option>
                    <option value="2">Semester 2</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Overview or instructions for candidates taking this standalone COC exam…"
                  rows={3}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Time Limit (Minutes)</label>
                  <input
                    type="number"
                    value={timeLimit}
                    onChange={(e) => setTimeLimit(e.target.value)}
                    placeholder="60"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Pass Percentage (%)</label>
                  <input
                    type="number"
                    value={passPercentage}
                    onChange={(e) => setPassPercentage(e.target.value)}
                    placeholder="50"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border text-sm outline-none focus:border-primary"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="w-4 h-4 rounded text-primary"
                  />
                  Publish for students immediately
                </label>

                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={isFree}
                    onChange={(e) => setIsFree(e.target.checked)}
                    className="w-4 h-4 rounded text-primary"
                  />
                  Free access (unchecked = Premium required)
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Question Input Mode Selector */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40 border border-border">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-muted-foreground uppercase">Questions Source:</span>
                  <div className="flex items-center bg-card p-1 rounded-lg border border-border">
                    <button
                      type="button"
                      onClick={() => setInputMode("json")}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 ${
                        inputMode === "json"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <FileCode size={14} /> Paste/Upload JSON
                    </button>
                    <button
                      type="button"
                      onClick={() => setInputMode("form")}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 ${
                        inputMode === "form"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Plus size={14} /> Visual Form Builder
                    </button>
                  </div>
                </div>

                <span className="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {questions.length} Questions Loaded
                </span>
              </div>

              {/* JSON Input Section */}
              {inputMode === "json" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <label className="text-xs font-semibold text-muted-foreground">
                      Paste JSON Questions Array (or upload `.json` file):
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".json,application/json"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold hover:bg-muted inline-flex items-center gap-1.5"
                      >
                        <Upload size={13} /> Upload `.json` File
                      </button>
                      <button
                        type="button"
                        onClick={handleLoadSampleJson}
                        className="px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold hover:bg-muted inline-flex items-center gap-1.5 text-primary"
                      >
                        <Sparkles size={13} /> Load Sample JSON
                      </button>
                    </div>
                  </div>

                  <textarea
                    value={jsonInput}
                    onChange={(e) => {
                      setJsonInput(e.target.value);
                      setJsonError("");
                      setJsonSuccessMsg("");
                    }}
                    placeholder={`Paste JSON questions here, e.g.\n[\n  {\n    "text": "Question 1 prompt?",\n    "options": ["Choice A", "Choice B", "Choice C", "Choice D"],\n    "correct_answer": 0,\n    "explanation": "Why choice A is correct"\n  }\n]`}
                    rows={12}
                    className="w-full p-4 rounded-xl border border-border bg-card text-foreground font-mono text-xs outline-none focus:border-primary leading-relaxed shadow-inner"
                  />

                  {jsonError && (
                    <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs border border-destructive/30 flex items-start gap-2">
                      <AlertCircle size={16} className="shrink-0 text-destructive mt-0.5" />
                      <span>{jsonError}</span>
                    </div>
                  )}

                  {jsonSuccessMsg && (
                    <div className="p-3 rounded-lg bg-success/10 text-success text-xs border border-success/30 flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-success" />
                      <span>{jsonSuccessMsg}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-1">
                    <p className="text-xs text-muted-foreground">
                      Accepts flexible keys: <code className="bg-muted px-1 rounded">text</code> or <code className="bg-muted px-1 rounded">question</code>, <code className="bg-muted px-1 rounded">options</code> array, <code className="bg-muted px-1 rounded">correct_answer</code> (0-3 or "A"-"D").
                    </p>
                    <button
                      type="button"
                      onClick={handleApplyJson}
                      className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-bold shadow-sm hover:opacity-90 transition flex items-center gap-1.5"
                    >
                      <RefreshCw size={13} /> Parse & Apply Questions
                    </button>
                  </div>
                </div>
              )}

              {/* Form Input Section */}
              {inputMode === "form" && (
                <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-4">
                  <h4 className="font-bold text-sm text-primary flex items-center gap-2">
                    <Plus size={16} /> {editingQIndex !== null ? `Editing Question #${editingQIndex + 1}` : "Add New Multiple Choice Question"}
                  </h4>

                  {qFormError && (
                    <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs border border-destructive/30 flex items-center gap-2">
                      <AlertCircle size={15} className="shrink-0 text-destructive" />
                      <span>{qFormError}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">Question Prompt *</label>
                    <textarea
                      value={qText}
                      onChange={(e) => setQText(e.target.value)}
                      placeholder="Enter the question text…"
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                    />
                  </div>

                  <div className="pt-1 pb-1">
                    <AdminPassageSelector
                      scope={exam ? `coc:${exam.id}` : "coc:draft"}
                      selectedPassageId={qPassageId}
                      onSelectPassage={setQPassageId}
                      token={token || ""}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">
                      Question Figure (Optional / Reference Image)
                    </label>
                    <QuestionFigureUploader
                      token={token || ""}
                      figureStatus={qFigureStatus}
                      figureImage={qFigureImage}
                      figureAlt={qFigureAlt}
                      figureLocation={qFigureLocation}
                      onChange={(fig) => {
                        setQFigureStatus(fig.figure_status);
                        setQFigureImage(fig.figure_image);
                        setQFigureAlt(fig.figure_alt || "");
                        setQFigureLocation(fig.figure_location || null);
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Option A *</label>
                      <input
                        value={qOptA}
                        onChange={(e) => setQOptA(e.target.value)}
                        placeholder="Choice A"
                        className="w-full px-3 py-1.5 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Option B *</label>
                      <input
                        value={qOptB}
                        onChange={(e) => setQOptB(e.target.value)}
                        placeholder="Choice B"
                        className="w-full px-3 py-1.5 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Option C</label>
                      <input
                        value={qOptC}
                        onChange={(e) => setQOptC(e.target.value)}
                        placeholder="Choice C"
                        className="w-full px-3 py-1.5 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Option D</label>
                      <input
                        value={qOptD}
                        onChange={(e) => setQOptD(e.target.value)}
                        placeholder="Choice D"
                        className="w-full px-3 py-1.5 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Correct Choice</label>
                      <select
                        value={qCorrect}
                        onChange={(e) => setQCorrect(parseInt(e.target.value, 10))}
                        className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                      >
                        <option value={0}>Option A</option>
                        <option value={1}>Option B</option>
                        {qOptC && <option value={2}>Option C</option>}
                        {qOptD && <option value={3}>Option D</option>}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Explanation (Optional)</label>
                      <input
                        value={qExplanation}
                        onChange={(e) => setQExplanation(e.target.value)}
                        placeholder="Why is this choice correct?"
                        className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-card outline-none focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    {editingQIndex !== null && (
                      <button
                        type="button"
                        onClick={resetQuestionDraft}
                        className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted"
                      >
                        Cancel Edit
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveQuestion}
                      className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition"
                    >
                      {editingQIndex !== null ? "Update Question" : "+ Add Question"}
                    </button>
                  </div>
                </div>
              )}

              {/* Questions Preview List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-sm">Parsed Questions Preview ({questions.length})</h4>
                  {questions.filter((q) => q.figure_status === "required" && !q.figure_image).length > 0 && (
                    <span className="text-xs font-semibold text-warning bg-warning/10 border border-warning/30 px-2.5 py-1 rounded-full flex items-center gap-1.5">
                      <AlertCircle size={13} className="text-warning" />
                      {questions.filter((q) => q.figure_status === "required" && !q.figure_image).length} question(s) need an image
                    </span>
                  )}
                </div>

                {questions.filter((q) => q.figure_status === "required" && !q.figure_image).length > 0 && (
                  <div className="p-3 rounded-xl bg-warning/10 border border-warning/30 text-xs text-warning">
                    <p className="font-semibold">Checklist: {questions.filter((q) => q.figure_status === "required" && !q.figure_image).length} question(s) need an image</p>
                    <p className="text-xs text-warning/80 dark:text-warning/80 mt-0.5">
                      Upload the required figures using the visual form builder or include valid image paths before publishing this exam.
                    </p>
                  </div>
                )}

                {questions.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic p-4 text-center border border-dashed rounded-xl">
                    No questions loaded yet. Paste/upload JSON above or click "Load Sample JSON".
                  </p>
                ) : (
                  questions.map((q, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-border bg-card space-y-2 relative">
                      <div className="flex items-start justify-between gap-4">
                        <p className="font-semibold text-sm">
                          <span className="text-primary font-bold mr-2">Q{idx + 1}.</span> {q.text}
                        </p>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleEditQuestion(idx)}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="Edit question in form"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveQuestion(idx)}
                            className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                            title="Delete question"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {q.figure_image ? (
                        <QuestionFigure
                          image={q.figure_image}
                          alt={q.figure_alt}
                          location={q.figure_location}
                          className="my-2"
                        />
                      ) : q.figure_status === "required" ? (
                        <div className="text-xs text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded-md inline-block font-semibold">
                          Image required
                        </div>
                      ) : null}

                      <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                        {q.options.map((opt, oIdx) => (
                          <div
                            key={oIdx}
                            className={`p-2 rounded-lg border ${
                              oIdx === q.correct_answer
                                ? "border-success/30 bg-success/10 text-success font-semibold"
                                : "border-border text-muted-foreground"
                            }`}
                          >
                            <span className="font-bold mr-1.5">{String.fromCharCode(65 + oIdx)}.</span> {opt}
                          </div>
                        ))}
                      </div>
                      {q.explanation && (
                        <p className="text-xs text-muted-foreground bg-muted/40 p-2 rounded-lg italic mt-1">
                          Explanation: {q.explanation}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Modal Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted"
            >
              Cancel
            </button>
            <div className="flex items-center gap-3">
              {activeTab === "details" ? (
                <button
                  type="button"
                  onClick={() => setActiveTab("questions")}
                  className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/80"
                >
                  Next: Questions ({questions.length}) →
                </button>
              ) : (
                <Button
                  type="submit"
                  loading={saving}
                  className="px-6 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-bold shadow-md hover:opacity-90"
                >
                  {exam ? "Update COC Exam" : "Create COC Exam"}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
